import { useMemo, useCallback } from 'react';
import { Extension, StateField, StateEffect } from '@codemirror/state';
import { EditorView, gutter, GutterMarker } from '@codemirror/view';
import { GitBlameLine } from './useGit';

// Format relative time for blame display
function formatBlameTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDay === 0) return 'today';
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}w ago`;
  if (diffDay < 365) return `${Math.floor(diffDay / 30)}mo ago`;
  return `${Math.floor(diffDay / 365)}y ago`;
}

// Blame gutter marker
class BlameGutterMarker extends GutterMarker {
  constructor(
    readonly author: string,
    readonly date: string,
    readonly hash: string,
    readonly message: string
  ) {
    super();
  }

  eq(other: BlameGutterMarker): boolean {
    return this.hash === other.hash && this.author === other.author;
  }

  toDOM(): Node {
    const wrap = document.createElement('div');
    wrap.className = 'git-blame-gutter';
    wrap.title = `${this.message}\n${this.hash}`;

    const authorSpan = document.createElement('span');
    authorSpan.className = 'git-blame-author';
    authorSpan.textContent = this.author.slice(0, 10);

    const dateSpan = document.createElement('span');
    dateSpan.className = 'git-blame-date';
    dateSpan.textContent = formatBlameTime(this.date);

    wrap.appendChild(authorSpan);
    wrap.appendChild(dateSpan);

    return wrap;
  }
}

// State effect to set blame data
const setBlameDataEffect = StateEffect.define<GitBlameLine[]>();

// State field to store blame data
const blameDataField = StateField.define<GitBlameLine[]>({
  create: () => [],
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setBlameDataEffect)) {
        return effect.value;
      }
    }
    return value;
  },
});

// Create the blame gutter
function createBlameGutter(): Extension {
  return gutter({
    class: 'cm-git-blame-gutter',
    lineMarker(view, line) {
      const blameLines = view.state.field(blameDataField);
      const lineNum = view.state.doc.lineAt(line.from).number;
      const blameLine = blameLines.find(b => b.lineNumber === lineNum);

      if (blameLine) {
        return new BlameGutterMarker(
          blameLine.author,
          blameLine.date,
          blameLine.shortHash,
          blameLine.message
        );
      }
      return null;
    },
    lineMarkerChange(update) {
      // Re-render when blame data changes
      return update.transactions.some(tr =>
        tr.effects.some(e => e.is(setBlameDataEffect))
      );
    },
    initialSpacer: () => new BlameGutterMarker('Developer', new Date().toISOString(), 'abc123', 'Message'),
  });
}

type UseGitBlameParams = {
  enabled: boolean;
  blameData: GitBlameLine[];
};

export function useGitBlame({ enabled, blameData }: UseGitBlameParams) {
  // Create the extension when enabled
  const blameExtension = useMemo<Extension>(() => {
    if (!enabled) return [];
    return [
      blameDataField,
      createBlameGutter(),
    ];
  }, [enabled]);

  // Function to update blame data in editor
  const updateBlameData = useCallback((view: EditorView, data: GitBlameLine[]) => {
    view.dispatch({
      effects: setBlameDataEffect.of(data),
    });
  }, []);

  return {
    blameExtension,
    updateBlameData,
  };
}
